import {
  deserializeHtml,
  createPlateUIEditor,
  parseHtmlDocument,
} from '@udecode/plate';

import { generateUUID } from 'utils/general';
import { plugins } from '../contexts/fullEditor';
import { fonts } from '../components/dropdown/FontDropdown';

const checkIfParentsContain = (tagNames, element) => {
  const { parentElement } = element;
  if (parentElement) {
    if (tagNames.includes(parentElement.tagName)) {
      return true;
    }
    return checkIfParentsContain(tagNames, parentElement);
  }
  return false;
};

const isSlateElementEmptyBreak = (element) => {
  if (element.type === 'p' && element.children.length === 1) {
    const { children } = element;
    const child = children[0];
    if (child.text === '') {
      return true;
    }
  }
  return false;
};

// This handles migrating content that uses divs instead of paragraph elements.
// We need to make sure that each div creates a line break otherwise the content runs onto each other.
const addCarriageReturnToLastChild = (element, isInsideP) => {
  const isParagraph = element.nodeName === 'P';
  if (element.hasChildNodes()) {
    const { childNodes } = element;
    const lastIndex = childNodes.length - 1;
    return addCarriageReturnToLastChild(childNodes[lastIndex], isParagraph);
  }

  // nodeType 1 is ELEMENT_NODE. If we reach here, then we are at an empty html element.
  // If not wrapped by a paragraph, create that now.
  if (element.nodeType === 1) {
    if (!isParagraph && !isInsideP) {
      const p = document.createElement('p');
      element.appendChild(p);
      return;
    }
  }
  // nodeType 3 is TEXT_NODE.
  if (element.nodeType === 3) {
    // Need to wrap break with a paragraph if not already within a paragraph.
    // This ensures empty divs create a new slate element when serialized.
    if (!isInsideP && element.textContent.length === 0) {
      const p = document.createElement('p');
      element.parentElement.appendChild(p);
    }
    const br = document.createElement('br');
    element.parentElement.appendChild(br);
  }
};

const deserializeTinyMCE = (element) => {
  // Deserialize html from element variable, if exists.
  let fragment;
  const parsedDocument = parseHtmlDocument(element);
  if (parsedDocument) {
    // Clean up non-breaking space followed by <br>
    const breaks = parsedDocument.querySelectorAll('br');
    if (breaks.length > 0) {
      for (let i = 0; i < breaks.length; i += 1) {
        const { parentNode } = breaks[i];
        if (parentNode?.childNodes) {
          // Convert <br> as only child within element to an empty paragraph
          // to prevent extra large spacing.
          if (parentNode.childNodes.length === 1) {
            parentNode.childNodes[0].remove();

            // We dont want <p> inside of <p> so we iterate through all the parents to check.
            if (parentNode.tagName !== 'P' && !checkIfParentsContain(['P', 'DIV'], parentNode)) {
              const empty = document.createElement('p');
              parentNode.appendChild(empty);
            }
          }
          // We want to look for elements with content followed by a <br>
          // then remove the <br>, so slate doesn't create an empty double line.
          else if (parentNode.childNodes.length === 2) {
            // eslint-disable-next-line
            const [first, second] = parentNode.childNodes;
            if (second.tagName === 'BR') {
              parentNode.childNodes[1].remove();
            }
          }
        }
      }
    }

    // Clean up styling marks in &nbsp; spans within paragraphs that act as a return.
    const spans = parsedDocument.querySelectorAll('span');
    if (spans.length > 0) {
      for (let i = 0; i < spans.length; i += 1) {
        const { innerText, parentNode } = spans[i];
        // Match ' ' and &nbsp; and ''.
        if (innerText.match(/^(\s)$/g) || innerText === '') {
          // We only want to remove spans with empty space if the empty space is the only child.
          // Text and nodes are considered children, so if length is 1, then that is the text.
          if (parentNode.childNodes.length === 1 && spans[i].childNodes.length < 2) {
            // Ensure child of span is a text node, not an image or other node that emits '' for inner text.
            // Also if span has no children.
            if (spans[i].childNodes.length === 0 || spans[i].childNodes[0].nodeType === 3) {
              parentNode.childNodes[0].remove();
              parentNode.textContent = ' ';
            }
          }

          // #TODO: Need to further refine this to clean up empty space next to images so that
          // <p> doesn't become nested within <p> when serializing in slate.
        }
      }
    }

    // Slate serializer handles divs as leaf elements. We need to make sure that a carriage return
    // is added to div content so that they are on a new line.
    const divs = parsedDocument.querySelectorAll('div');
    if (divs.length > 0) {
      for (let i = 0; i < divs.length; i += 1) {
        addCarriageReturnToLastChild(divs[i]);
      }
    }
    // Remove bogus.
    /* This might not be needed. Bogus seems to come from empty input in mce editor.
    const bogus = parsedDocument.querySelectorAll('[data-mce-bogus]');
    if (bogus.length > 0) {
      for (let i = 0; i < bogus.length; i += 1) {
        bogus[i].remove();
      }
    }
    */

    fragment = deserializeHtml(
      createPlateUIEditor({ plugins }),
      { element: parsedDocument.body },
    );

    // If a migration occured with divs, extra line breaks will need to be cleaned up
    // from the end of paragraph nodes in the serialized content. This occurs because
    // we naively add breaks to the end of all content within a div. However, if an empty
    // div is used to make a double return, then we need to remove the previous line break
    // so we don't get a double space between paragraphs.
    if (divs.length > 0 && fragment.length > 0) {
      const fragLength = fragment.length;
      for (let i = 0; i < fragLength; i += 1) {
        const slateElement = fragment[i];

        // We only care about empty elements that serve as a hard return.
        // We want to look at the previous element to remove unneeded line break.
        if (isSlateElementEmptyBreak(slateElement) && i > 0) {
          const prevElementIndex = i - 1;
          const prevElement = fragment[prevElementIndex];
          const { children } = prevElement;

          if (children && children.length > 1) {
            const lastChildIndex = children.length - 1;
            const lastChild = children[lastChildIndex];

            if (Object.prototype.hasOwnProperty.call(lastChild, 'text') && lastChild.text === '\n') {
              // Get the element we need to update.
              const front = fragment.slice(0, prevElementIndex);
              const middle = fragment[prevElementIndex];
              const end = fragment.slice(prevElementIndex + 1);
              // Remove the uneeded break and reconstruct the fragment.
              const newMiddle = { ...middle, children: middle.children.slice(0, -1) };
              fragment = [...front, newMiddle, ...end];
            }
          }
        }
      }
    }

    // migrate from TinyMCE adding marks, updating headers, etc.
    fragment.forEach((node) => {
      // Update font family of heading to match old notes.
      const headings = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
      const isHeading = headings.includes(node.type);
      if (isHeading) {
        node.fontFamily = 'Playfair Display';
      }

      // Generate an id for drag n drop.
      node.id = generateUUID();

      // Add default marks to retain 1-to-1 with old note.
      node.children = addDefaultMarks(node, isHeading);

    });

    return fragment;
  }

  return [];
};

// When migrating, default font should be Open Sans to keep the font the same.
const addDefaultMarks = (node, isHeading) => {
  const { children } = node;
  const modifiedChildren = children.map(child => {
    if (child.children) {
      // Add id to child if it is a node.
      return { ...child, id: generateUUID(), children: addDefaultMarks(child, isHeading) };
    }

    // Is a leaf node.
    if (child.text) {
      // Add default font size, if no font size set.
      const { fontFamily, fontSize, text } = child;

      // Remove styles from new lines
      if (text === '\n' || text.trim === '') {
        return { text: child.text };
      }

      if (!fontSize && !isHeading) {
        child.fontSize = '14px';
      }
      // For migrating notes, we default to open sans if no style is applied.
      if (!fontFamily && !isHeading) {
        return { ...child, fontFamily: 'Open Sans' };
      }

      // If font family is an allowed value, then we can return without doing anything.
      if (fonts.includes(fontFamily)) {
        return child;
      }

      // Otherwise return child without font family (uses editor default).
      delete child.fontFamily;
      return child;
    }
    // Shouldn't happen.
    return child;
  });
  return modifiedChildren;
};

export default deserializeTinyMCE;
